import type { Fetcher } from '@project/common';
import { useChromeExtension } from '@project/common/app';
import RootApp from '@project/common/app/components/RootApp';
import { useEffect, useMemo } from 'react';
import { AppExtensionDictionaryStorage } from '@project/common/app/services/app-extension-dictionary-storage';
import { AppExtensionSettingsStorage } from '@project/common/app/services/app-extension-settings-storage';
import { AppExtensionGlobalStateProvider } from '@project/common/app/services/app-extension-global-state-provider';
import { SettingsProvider } from '@project/common/settings';
import { configureLogProvider, LogProvider } from '@project/common/util/log';
import { LocalDictionaryStorage } from '@project/client/src/local-dictionary-storage';
import { LocalSettingsStorage } from '@project/client/src/local-settings-storage';
import { LocalLogStorage } from '@project/client/src/local-log-storage';
import { AppExtensionLogStorage } from '@project/common/app/services/app-extension-log-storage';

interface Props {
    origin: string;
    logoUrl: string;
    fetcher: Fetcher;
}

const WebsiteApp = (props: Props) => {
    const extension = useChromeExtension({ component: 'application' });
    const settingsStorage = useMemo(() => {
        if (extension.supportsAppIntegration) return new AppExtensionSettingsStorage(extension);
        return new LocalSettingsStorage();
    }, [extension]);
    useEffect(() => {
        if (extension.version) window.plausible?.('extension_version', { props: { version: extension.version } });
    }, [extension.version]);
    const settingsProvider = useMemo(() => new SettingsProvider(settingsStorage), [settingsStorage]);
    const logStorage = useMemo(() => {
        if (extension.supportsLogs) return new AppExtensionLogStorage(extension);
        return new LocalLogStorage();
    }, [extension]);
    const logProvider = useMemo(() => new LogProvider(logStorage), [logStorage]);
    useEffect(() => {
        void configureLogProvider(logProvider);
    }, [logProvider]);
    const dictionaryStorage = useMemo(() => {
        if (extension.supportsDictionary) return new AppExtensionDictionaryStorage(extension);
        return new LocalDictionaryStorage(settingsProvider);
    }, [extension, settingsProvider]);
    const globalStateProvider = useMemo(() => new AppExtensionGlobalStateProvider(extension), [extension]);
    return (
        <RootApp
            {...props}
            extension={extension}
            dictionaryStorage={dictionaryStorage}
            settingsStorage={settingsStorage}
            settingsProvider={settingsProvider}
            globalStateProvider={globalStateProvider}
            logProvider={logProvider}
        />
    );
};

export default WebsiteApp;
